// Payment frequency vocabulary and per-product terms — frontend mirror.
//
// NO React or Redux imports in this file. reviseSeed.js imports it and is
// loaded directly by a Node test (tests/rcProActivityProration.test.mjs),
// so a bundler-only dependency here breaks that suite.

// ─── Payment terms ────────────────────────────────────────────────────
//
// Mirror of backend/services/paymentTerms.js, for the same reason the
// product/contract-term defaults are mirrored here: the quote form has to
// render its dropdown before tenantConfig has hydrated. Keep the two in
// sync — the backend is the authority and re-validates every submission,
// so a stale mirror narrows the UI but can never widen what is accepted.

export const PAYMENT_FREQUENCIES = ['annual', 'semiAnnual', 'quarterly', 'monthly'];

/** i18n keys for the canonical frequency codes. */
export const PAYMENT_FREQUENCY_LABEL_KEYS = {
  annual:     'offers.pgm.paymentAnnual',
  semiAnnual: 'offers.pgm.paymentSemi',
  quarterly:  'offers.pgm.paymentQuarter',
  monthly:    'offers.pgm.paymentMonthly',
};

/** French/English/German display strings written by earlier builds. */
// Null-prototype: as a plain literal this answers map['__proto__'] with
// Object.prototype, so normalisePaymentFrequency returned a truthy non-code.
export const LEGACY_FREQUENCY_LABELS = Object.assign(Object.create(null), {
  Annuel: 'annual', Annual: 'annual', 'Jährlich': 'annual',
  Semestriel: 'semiAnnual', 'Semi-annual': 'semiAnnual', 'Halbjährlich': 'semiAnnual',
  Trimestriel: 'quarterly', Quarterly: 'quarterly', 'Vierteljährlich': 'quarterly',
  Mensuel: 'monthly', Monthly: 'monthly', 'Monatlich': 'monthly',
});

/** Normalise a stored frequency to a code; '' when unrecognised. */
export function normalisePaymentFrequency(stored) {
  if (typeof stored !== 'string' && typeof stored !== 'number') return '';
  const raw = String(stored).trim();
  if (!raw) return '';
  if (PAYMENT_FREQUENCIES.includes(raw)) return raw;
  const mapped = LEGACY_FREQUENCY_LABELS[raw];
  return typeof mapped === 'string' && PAYMENT_FREQUENCIES.includes(mapped) ? mapped : '';
}

/**
 * The payment terms in force for one product. Mirrors resolvePaymentTerms
 * on the backend, including the platform default (annual only, anniversary
 * derived from inception) applied when the tenant has configured nothing.
 */
export function resolvePaymentTerms(config, productCode) {
  const code = String(productCode || '').trim();
  const all = config?.paymentTerms;
  const raw = code && all ? all[code] : null;
  // frequenciesAllowed === null means UNRESTRICTED: the tenant has configured
  // no restriction, so the form keeps whatever options it offered before.
  // Defaulting this to ['annual'] silently narrowed unconfigured tenants.
  const fallback = { frequency: 'annual', frequenciesAllowed: null, dueDateMode: 'derived', principalDueDate: null, fractionalMinimumPremium: null, instalmentSurcharge: null };
  if (!raw) return fallback;

  const allowed = Array.isArray(raw.frequenciesAllowed)
    ? [...new Set(raw.frequenciesAllowed.map(normalisePaymentFrequency).filter(Boolean))]
    : [];
  const frequenciesAllowed = allowed.length ? allowed : null;
  const wanted = normalisePaymentFrequency(raw.frequency);
  const floorRaw = Number(raw.fractionalMinimumPremium);
  return {
    frequency: frequenciesAllowed
      ? (wanted && frequenciesAllowed.includes(wanted) ? wanted : frequenciesAllowed[0])
      : (wanted || fallback.frequency),
    frequenciesAllowed,
    dueDateMode: raw.dueDateMode === 'fixed' && raw.principalDueDate ? 'fixed' : 'derived',
    principalDueDate: raw.principalDueDate || null,
    fractionalMinimumPremium: Number.isFinite(floorRaw) && floorRaw > 0 ? floorRaw : null,
    instalmentSurcharge: raw.instalmentSurcharge || null,
  };
}

/**
 * Frequencies the quote form may offer: allowed by the tenant AND eligible at
 * this premium. Mirror of availableFrequencies in
 * backend/services/paymentTerms.js -- the backend re-validates every
 * submission, so a stale mirror can narrow the dropdown but never widen what
 * the API will accept.
 *
 * Annual always survives: a premium that cannot be paid at all is not a quote.
 * An unknown premium (the form before it has rated) offers annual only, so an
 * instalment can never be selected and then invalidated by the rated premium.
 */
export function availableFrequencies(terms, annualPremium) {
  // NOT CONFIGURED = ANNUAL ONLY (2026-09-28), as on the backend: an
  // unconfigured product used to offer every frequency including monthly,
  // with no surcharge. Instalments exist only where a tenant configures them.
  const allowed = terms?.frequenciesAllowed || ['annual'];
  const floor = terms?.fractionalMinimumPremium;
  const premium = Number(annualPremium);
  const out = allowed.filter((f) => {
    if (f === 'annual') return true;
    if (floor == null) return true;
    return Number.isFinite(premium) && premium > floor;
  });
  return out.length ? out : ['annual'];
}


// ─── Instalment preview (mirror of computePaymentSchedule) ────────────
//
// What the form shows under the selector. The backend recomputes and stores
// the schedule on save; this previews the same arithmetic so the operator
// sees the surcharge BEFORE creating the quote.

const INSTALMENTS_PER_YEAR = { annual: 1, semiAnnual: 2, quarterly: 4, monthly: 12 };
const round2 = (n) => Math.round(n * 100) / 100;

/** The surcharge figure for one frequency: per-frequency first, then flat. */
export function surchargeFor(terms, frequency) {
  const code = normalisePaymentFrequency(frequency);
  if (!code || INSTALMENTS_PER_YEAR[code] === 1) return null;
  const sc = terms?.instalmentSurcharge;
  if (!sc) return null;
  const per = sc.byFrequency ? sc.byFrequency[code] : null;
  if (per && per.type && per.value != null) return { type: per.type, value: Number(per.value) };
  return sc.type && sc.value != null ? { type: sc.type, value: Number(sc.value) } : null;
}

/**
 * { instalments, surcharge, rate, total, perInstalment } for a frequency, or
 * null while the premium is unknown. PERCENT IS OF THE TOTAL ANNUAL PREMIUM:
 * 3% of CHF 12,000 is CHF 360 once, split across the instalments.
 */
export function previewPaymentSchedule(annualPremium, terms, frequency) {
  const code = normalisePaymentFrequency(frequency) || 'annual';
  const n = INSTALMENTS_PER_YEAR[code] || 1;
  const base = Number(annualPremium);
  if (annualPremium == null || !Number.isFinite(base) || base < 0) return null;
  const fig = surchargeFor(terms, code);
  const surcharge = n === 1 || !fig ? 0 : (fig.type === 'percent' ? (base * fig.value) / 100 : fig.value);
  const total = round2(base + surcharge);
  return {
    frequency: code,
    instalments: n,
    surcharge: n === 1 ? null : round2(surcharge),
    rate: fig && fig.type === 'percent' ? fig.value : null,
    total,
    perInstalment: round2(total / n),
  };
}

/**
 * Everything a quote form needs to render the frequency control, the same
 * way on RC Pro, PGMA and PGM:
 *   options   the frequencies offerable now
 *   disabled  true when annual is the only option
 *   hint      why: { key: 'notConfigured' } when the tenant configured no
 *             instalments for the product, { key: 'belowThreshold', amount }
 *             when the premium does not exceed the configured floor
 */
export function frequencyControlState(terms, annualPremium) {
  const options = availableFrequencies(terms, annualPremium);
  const disabled = options.length <= 1;
  let hint = null;
  if (disabled) {
    const fractionalConfigured = (terms?.frequenciesAllowed || []).some((f) => INSTALMENTS_PER_YEAR[f] > 1);
    if (!fractionalConfigured) hint = { key: 'notConfigured' };
    else if (terms.fractionalMinimumPremium != null) hint = { key: 'belowThreshold', amount: terms.fractionalMinimumPremium };
  }
  return { options, disabled, hint };
}
